import { useState, type MouseEvent } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { putFetcher } from "api/swr";

export function RemoveDefaultSetButton({
  avatarId,
  onRemoved,
}: {
  avatarId: string;
  onRemoved: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleConfirm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;
    setPending(true);
    try {
      await putFetcher(`/spine-avatars/${avatarId}/default-set`, {
        arg: { defaultSet: 0 },
      });
      toast({
        title: "已移除套装",
        description: avatarId,
        className: "bg-green-500 text-white",
      });
      setOpen(false);
      await onRemoved();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "移除失败",
        description: err instanceof Error ? err.message : "未知错误",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0 border-destructive bg-background text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive"
        onClick={handleOpen}
        title="移除套装"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!pending) setOpen(next);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>确定移除该套装？</DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={pending}
            >
              {pending ? "处理中…" : "确认移除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
